<script setup lang="ts">
import { parseJsonb } from '~/utils/eclipse'
import { safeJsonLd } from '~/utils/jsonLd'
import { PROFILES, useRecommendation } from '~/composables/useRecommendation'
import type { ProfileId, RankedSpot } from '~/composables/useRecommendation'
import type { Region, SpotPhoto } from '~/types/spots'

const { coords } = useLocation()
const route = useRoute()
const router = useRouter()

const { t, locale } = useI18n()
const { data } = await useFetch('/api/spots', {
  // view=list trims the payload to hero-thumb + horizon verdict (the only
  // fields this grid uses), cutting ~240 KB of unused photo metadata off
  // the route's critical path. See server/api/spots/index.get.ts.
  query: { locale: locale.value, view: 'list' },
  key: `spots-list-${locale.value}`,
})

// Historical cloud cover — pre-computed
const { data: historicalData } = useFetch<{ spots: Record<string, { clear_years: number; total_years: number; avg_cloud_cover: number | null }> }>(
  '/eclipse-data/historical-weather.json',
  { lazy: true, server: false, key: 'historical-weather' },
)
function historyFor(slug: string) {
  return historicalData.value?.spots?.[slug] ?? null
}

// Sort mode — URL-persisted so deep links preserve it
type SortKey = 'duration' | 'historical'
const initialSort = typeof route.query.sort === 'string' ? route.query.sort : null
const sortKey = ref<SortKey>(initialSort === 'historical' ? 'historical' : 'duration')

watch(sortKey, (val) => {
  if (!import.meta.client) return
  const query = { ...route.query }
  if (val === 'historical') query.sort = 'historical'
  else delete query.sort
  router.replace({ path: route.path, query })
})

// Region filter — URL-persisted
const initialRegion = typeof route.query.region === 'string' ? route.query.region : null
const VALID_REGIONS: ReadonlyArray<Region> = ['westfjords', 'snaefellsnes', 'borgarfjordur', 'reykjavik', 'reykjanes']
const selectedRegion = ref<Region | null>(
  VALID_REGIONS.includes(initialRegion as Region) ? (initialRegion as Region) : null,
)
watch(selectedRegion, (val) => {
  if (!import.meta.client) return
  const query = { ...route.query }
  if (val) query.region = val
  else delete query.region
  router.replace({ path: route.path, query })
})

const rawSpots = computed(() => {
  const list = data.value?.spots || []
  const copy = [...list]
  if (sortKey.value === 'historical') {
    return copy.sort((a: any, b: any) => {
      const ha = historyFor(a.slug)
      const hb = historyFor(b.slug)
      const clearA = ha?.clear_years ?? -1
      const clearB = hb?.clear_years ?? -1
      if (clearA !== clearB) return clearB - clearA
      const avgA = ha?.avg_cloud_cover ?? 100
      const avgB = hb?.avg_cloud_cover ?? 100
      if (avgA !== avgB) return avgA - avgB
      return (b.totality_duration_seconds || 0) - (a.totality_duration_seconds || 0)
    })
  }
  return copy.sort((a: any, b: any) => (b.totality_duration_seconds || 0) - (a.totality_duration_seconds || 0))
})

// Weather + stations — lazy, client-only
const { data: rawWeatherData } = useFetch<{ cloud_cover: Array<{ station_id: string; cloud_cover: number | null }> }>('/api/weather/cloud-cover', { lazy: true, server: false })
const { data: rawStationsData } = useFetch<{ stations: Array<{ id: string; lat: number; lng: number }> }>('/api/weather/stations', { lazy: true, server: false })

const weatherData = computed(() => rawWeatherData.value?.cloud_cover || null)
const stationsData = computed(() => rawStationsData.value?.stations || null)

// Profile selection — URL-persisted
const initialProfile = typeof route.query.profile === 'string' ? route.query.profile : null
const selectedProfile = ref<ProfileId | null>(
  PROFILES.some(p => p.id === initialProfile) ? (initialProfile as ProfileId) : null,
)
watch(selectedProfile, (val) => {
  if (!import.meta.client) return
  const query = { ...route.query }
  if (val) query.profile = val
  else delete query.profile
  router.replace({ path: route.path, query })
})

// The profile picker was Pro-gated until 2026-08-13. Eclipse Pro is
// retired, so every visitor gets the ranker — there is nothing to
// upsell and the upgrade prompt has been removed.
const profileModel = computed<ProfileId | null>({
  get: () => selectedProfile.value,
  set: (v) => { selectedProfile.value = v },
})

// Recommendation engine
const { ranked, thinResults } = useRecommendation(
  rawSpots,
  weatherData,
  stationsData,
  coords,
  selectedProfile,
)

// Region filter applied AFTER ranking so profile sort is preserved
const displayItems = computed<RankedSpot[]>(() => {
  if (!selectedRegion.value) return ranked.value
  return ranked.value.filter(item => item.spot.region === selectedRegion.value)
})

// Helpers
function heroFilenameFor(spot: any): string | null {
  const photos = parseJsonb<SpotPhoto[]>(spot.photos, [])
  const hero = photos.find(p => p.is_hero) || photos[0]
  return hero?.filename ?? null
}
function heroAltFor(spot: any): string {
  const photos = parseJsonb<SpotPhoto[]>(spot.photos, [])
  const hero = photos.find(p => p.is_hero) || photos[0]
  return hero?.alt ?? spot.name
}
function cloudFor(spot: any): number | null {
  const h = historyFor(spot.slug)
  return h?.avg_cloud_cover ?? null
}

defineOgImageComponent('Default', {
  label: t('spots_page.eyebrow').toUpperCase(),
  title: t('spots_page.title'),
  subtitle: t('spots_page.description'),
})

const siteUrl = useRuntimeConfig().public.siteUrl as string

// Localised canonical URL for the list page itself + a helper for each
// spot's detail URL. `prefix_except_default` strategy: EN lives at
// /spots, IS at /is/spots.
const localePrefix = computed(() => (locale.value === 'en' ? '' : `/${locale.value}`))
const canonicalListUrl = computed(() => canonicalize(`${siteUrl}${localePrefix.value}/spots`))
function spotDetailUrl(slug: string) {
  return canonicalize(`${siteUrl}${localePrefix.value}/spots/${slug}`)
}

// JSON-LD ItemList — uses the API's canonical order (totality duration
// desc), not the user's UI-filtered order. Crawlers should see the
// stable list, not whatever profile / region filter is in the URL.
const itemListElements = computed(() =>
  (data.value?.spots ?? []).map((spot: any, i: number) => {
    const photos = parseJsonb<SpotPhoto[]>(spot.photos, [])
    const hero = photos.find(p => p.is_hero) || photos[0]
    return {
      '@type': 'ListItem',
      'position': i + 1,
      'item': {
        '@type': 'TouristAttraction',
        'name': spot.name,
        'url': spotDetailUrl(spot.slug),
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': spot.lat,
          'longitude': spot.lng,
        },
        'isAccessibleForFree': true,
        ...(hero?.filename
          ? { 'image': `${siteUrl}/images/spots/${hero.filename}` }
          : {}),
      },
    }
  }),
)

useHead(() => ({
  title: () => t('spots_page.title'),
  meta: [
    { name: 'description', content: () => t('spots_page.description') },
    { property: 'og:url', content: canonicalListUrl.value },
    { property: 'og:type', content: 'website' },
  ],
  link: [
    { rel: 'canonical', href: canonicalListUrl.value },
  ],
  script: [
    {
      type: 'application/ld+json',
      // CollectionPage wraps the ItemList so crawlers understand this
      // is a curated index page, not a single article. Each TouristAttraction
      // entry mirrors the shape used on /spots/[slug] for consistency.
      innerHTML: safeJsonLd({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': t('spots_page.title'),
        'description': t('spots_page.description'),
        'url': canonicalListUrl.value,
        'inLanguage': locale.value,
        'mainEntity': {
          '@type': 'ItemList',
          'numberOfItems': itemListElements.value.length,
          'itemListElement': itemListElements.value,
        },
      }),
    },
  ],
}))

const headerSub = computed(() => {
  const p = PROFILES.find(p => p.id === selectedProfile.value)
  const profileName = p ? t(p.nameKey) : t('recommend.profile_names.all')
  const sortLabel = sortKey.value === 'duration'
    ? t('spots_page.sort_by_duration')
    : t('spots_page.sort_by_clearness')
  return t('spots_page.header_sub', { profile: profileName, sort: sortLabel })
})
</script>

<template>
  <PageShell screen="spots">
    <header class="spots-header">
      <!-- Visually-hidden H1 for SEO + a11y: the v0 design uses the eyebrow
           as the visual label and has no big title, but the page still needs
           exactly one descriptive heading. -->
      <h1 class="sr-only">{{ t('spots_page.h1') }}</h1>
      <Eyebrow variant="dot" tone="accent">{{ t('spots_page.eyebrow').toUpperCase() }} · {{ displayItems.length }}</Eyebrow>
      <p class="spots-sub">{{ headerSub }}</p>
    </header>

    <ProfileSelector v-model="profileModel" />
    <SortTabs v-model="sortKey" :disabled="!!selectedProfile" />
    <RegionChips v-model="selectedRegion" />

    <div v-if="selectedProfile && thinResults" class="thin-results">
      {{ t('spots_page.thin_results') }}
    </div>

    <div class="spots-list">
      <SpotCard
        v-for="(item, i) in displayItems"
        :key="item.spot.id"
        :slug="item.spot.slug"
        :name="item.spot.name"
        :region="item.spot.region"
        :duration-seconds="item.spot.totality_duration_seconds || 0"
        :cloud="cloudFor(item.spot)"
        :hero-filename="heroFilenameFor(item.spot)"
        :hero-alt="heroAltFor(item.spot)"
        :eager="i < 4"
        :class="{ 'is-filtered': item.filtered }"
      />
    </div>
  </PageShell>
</template>

<style scoped>
.spots-header {
  padding: 24px 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.spots-sub {
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 14px;
  color: rgb(var(--ink-1) / 0.62);
  margin: 0;
}

.thin-results {
  margin: 12px 16px;
  padding: 10px 12px;
  background: rgb(var(--warn) / 0.1);
  border: 1px solid rgb(var(--warn) / 0.4);
  border-radius: 8px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  color: rgb(var(--ink-1));
}

.spots-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 16px;
}
@media (min-width: 640px) {
  .spots-list { grid-template-columns: repeat(2, 1fr); gap: 16px; padding: 16px 24px; }
  .spots-header { padding: 32px 24px 18px; }
  .spots-sub { font-size: 16px; }
}

.is-filtered {
  opacity: 0.5;
}
</style>
